import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Bill, BillSummary } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface BillsState {
  bills: Bill[];
  upcoming: Bill[];
  overdue: Bill[];
  summary: BillSummary | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: BillsState = {
  bills: [],
  upcoming: [],
  overdue: [],
  summary: null,
  isLoading: false,
  error: null,
};

export const fetchBills = createAsyncThunk(
  'bills/fetchBills',
  async (params: { status?: string; page?: number; limit?: number; sort?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.bills.list, { params });
      return (response.data.data || response.data) as Bill[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch bills'));
    }
  }
);

export const fetchBill = createAsyncThunk(
  'bills/fetchBill',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.bills.get(id));
      return (response.data.data || response.data) as Bill;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch bill'));
    }
  }
);

export const createBill = createAsyncThunk(
  'bills/createBill',
  async (data: Partial<Bill>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.bills.create, data);
      return (response.data.data || response.data) as Bill;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create bill'));
    }
  }
);

export const updateBill = createAsyncThunk(
  'bills/updateBill',
  async ({ id, data }: { id: string; data: Partial<Bill> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.bills.update(id), data);
      return (response.data.data || response.data) as Bill;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update bill'));
    }
  }
);

export const deleteBill = createAsyncThunk(
  'bills/deleteBill',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.bills.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete bill'));
    }
  }
);

export const markBillAsPaid = createAsyncThunk(
  'bills/markBillAsPaid',
  async ({ id, paidAmount, accountId }: { id: string; paidAmount?: number; accountId?: string }, { rejectWithValue }) => {
    try {
      const response = await api.patch(endpoints.bills.pay(id), { paidAmount, accountId });
      return (response.data.data || response.data) as Bill;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to mark bill as paid'));
    }
  }
);

export const generateNextBill = createAsyncThunk(
  'bills/generateNextBill',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.bills.generateNext(id));
      return (response.data.data || response.data) as Bill;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to generate next bill'));
    }
  }
);

export const fetchBillSummary = createAsyncThunk(
  'bills/fetchBillSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.bills.summary);
      return (response.data.data || response.data) as BillSummary;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch bill summary'));
    }
  }
);

export const fetchUpcomingBills = createAsyncThunk(
  'bills/fetchUpcomingBills',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.bills.upcoming);
      return (response.data.data || response.data) as Bill[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch upcoming bills'));
    }
  }
);

export const fetchOverdueBills = createAsyncThunk(
  'bills/fetchOverdueBills',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.bills.overdue);
      return (response.data.data || response.data) as Bill[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch overdue bills'));
    }
  }
);

const billsSlice = createSlice({
  name: 'bills',
  initialState,
  reducers: {
    clearBillsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBills.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchBills.fulfilled, (state, action) => { state.isLoading = false; state.bills = action.payload; });
    builder.addCase(fetchBills.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    builder.addCase(createBill.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(createBill.fulfilled, (state, action) => { state.isLoading = false; state.bills.push(action.payload); });
    builder.addCase(createBill.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    builder.addCase(updateBill.fulfilled, (state, action) => {
      const idx = state.bills.findIndex(b => b._id === action.payload._id);
      if (idx !== -1) state.bills[idx] = action.payload;
    });
    builder.addCase(updateBill.rejected, (state, action) => { state.error = action.payload as string; });

    builder.addCase(deleteBill.fulfilled, (state, action) => {
      state.bills = state.bills.filter(b => b._id !== action.payload);
    });
    builder.addCase(deleteBill.rejected, (state, action) => { state.error = action.payload as string; });

    builder.addCase(markBillAsPaid.fulfilled, (state, action) => {
      const idx = state.bills.findIndex(b => b._id === action.payload._id);
      if (idx !== -1) state.bills[idx] = action.payload;
    });
    builder.addCase(markBillAsPaid.rejected, (state, action) => { state.error = action.payload as string; });

    builder.addCase(generateNextBill.fulfilled, (state, action) => {
      state.bills.push(action.payload);
    });

    builder.addCase(fetchBillSummary.fulfilled, (state, action) => { state.summary = action.payload; });

    builder.addCase(fetchUpcomingBills.fulfilled, (state, action) => { state.upcoming = action.payload; });

    builder.addCase(fetchOverdueBills.fulfilled, (state, action) => { state.overdue = action.payload; });
  },
});

export const { clearBillsError } = billsSlice.actions;
export default billsSlice.reducer;
